import { logger, sanitizeError } from '@heidi/runtime/logger';
import { TrackingKey } from '@heidi/runtime/performance';
import { GenerationMethod } from '@heidi/schema/ml-scribe';
import type { QueryClient } from '@tanstack/react-query';
import type {
  GetTemplateCreationRecordResponse,
  TemplateResponse,
  TemplateVersionControlResponse,
} from '../../contracts';
import { GET_TEMPLATE_BY_ID, GET_TEMPLATE_CREATION_RECORD_BY_ID } from '../../contracts';
import {
  getTemplateById,
  getTemplateCreationRecordByTemplateId,
  type createLatestVersionControl,
  type createTemplateCreationRecordByTemplateId,
  type updateTemplateCreationRecord,
} from '../../data/api/templates';
import { templateCreationRecordByTemplateIdQueryOptions } from '../../data/query/useQueryTemplateCreationRecordByTemplateId';
import { trackStreamRun } from '../template-streaming/streamPerfTracking';
import { streamExampleFromStructureGeneration } from '../template-streaming/streaming';
import {
  ERR_SECTION_CONVERSION_GUARDRAIL_FAILED,
  ERR_TEMPLATE_STREAM_UPSTREAM_TIMEOUT,
  ERR_UNSUPPORTED_TEMPLATE_STRUCTURE_REQUEST,
} from '../template-streaming/templateStreamEnvelope';

const knownStreamErrorCodes = new Set([
  ERR_SECTION_CONVERSION_GUARDRAIL_FAILED,
  ERR_TEMPLATE_STREAM_UPSTREAM_TIMEOUT,
  ERR_UNSUPPORTED_TEMPLATE_STRUCTURE_REQUEST,
]);

function failValidation(
  reason: 'unprepared_record' | 'incomplete_stream' | 'canonical_mismatch',
  message: string
): never {
  logger.error('[TEMPLATE_PREVIEW_EXAMPLE] Validation failed', { reason });
  throw new Error(message);
}

type RecordResponse = GetTemplateCreationRecordResponse;
type Ports = {
  queryClient: QueryClient;
  template: TemplateResponse;
  userRefId: string;
  createRecord: typeof createTemplateCreationRecordByTemplateId;
  updateRecord: typeof updateTemplateCreationRecord;
  createVersion: typeof createLatestVersionControl;
  onChunk: (example: string) => void;
};
type Bootstrap = {
  record?: RecordResponse;
  pending?: Promise<RecordResponse>;
  resume: () => Promise<RecordResponse>;
};

const withDiagnostics =
  <C, T>(
    phase: 'resolve_record' | 'prepare_version' | 'generate_example',
    run: (context: C, options: { signal: AbortSignal }) => Promise<T>
  ) =>
  async (context: C, options: { signal: AbortSignal }): Promise<T> => {
    try {
      return await run(context, options);
    } catch (error) {
      if (!options.signal.aborted)
        logger.error('[TEMPLATE_PREVIEW_EXAMPLE] Failed', {
          phase,
          error: sanitizeError(error),
        });
      throw error;
    }
  };

// Retain a successfully created setup record if linking fails. Retrying PATCH
// must not repeat the non-idempotent create, including after closing/reopening.
const bootstraps = new WeakMap<QueryClient, Map<string, Bootstrap>>();
const bootstrapKey = (templateId: string, userRefId: string) =>
  JSON.stringify([userRefId, templateId]);
const isLinkedSetup = (record: RecordResponse, templateId: string, userRefId: string) =>
  record.template_creation_record.template_id === templateId &&
  record.template_creation_record.user_ref_id === userRefId;

// Join only an existing durable setup, including retrying its retained failed
// link. A full-editor handoff must never create a record merely by waiting.
export async function awaitTemplatePreviewSetup(
  queryClient: QueryClient,
  templateId: string,
  userRefId: string
): Promise<void> {
  const entry = bootstraps.get(queryClient)?.get(bootstrapKey(templateId, userRefId));
  if (!entry) return;
  const linked = await (entry.pending ?? entry.resume());
  if (!isLinkedSetup(linked, templateId, userRefId))
    throw new Error('Preview setup was not linked for this owner and template');
}

export function findMatchingPreviewVersion(
  record: RecordResponse,
  template: TemplateResponse
) {
  const versions = record.version_controls.filter(
    (version) => version.template_structure === template.structure_template
  );
  const preferredId = record.template_creation_record.selected_version_control_id;
  return (
    versions.find((version) => version.id === preferredId && !!version.example_note) ??
    [...versions].reverse().find((version) => !!version.example_note) ??
    versions.find((version) => version.id === preferredId) ??
    versions.at(-1) ??
    null
  );
}

function assertActive(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Preview closed', 'AbortError');
}

export function createTemplatePreviewExampleServices(ports: Ports) {
  const { queryClient, template, userRefId } = ports;
  const options = templateCreationRecordByTemplateIdQueryOptions(template.id);
  let preparedRecord: RecordResponse | null = null;
  let confirmedTemplate = template;

  const confirmSavedStructure = async (signal: AbortSignal) => {
    const live = await queryClient.fetchQuery({
      queryKey: [GET_TEMPLATE_BY_ID, { templateId: template.id }, undefined],
      queryFn: async () => (await getTemplateById(template.id)).data,
      staleTime: 0,
      retry: false,
    });
    assertActive(signal);
    const matches =
      live.id === template.id && live.structure_template === template.structure_template;
    if (!matches) {
      queryClient.setQueriesData(
        {
          queryKey: [GET_TEMPLATE_BY_ID, { templateId: template.id }],
          predicate: (query) => query.queryKey[2] !== GenerationMethod.INTERNAL_TEMPLATE,
        },
        live
      );
    }
    if (matches) confirmedTemplate = live;
    return matches;
  };

  // Merge only the prepared version into each latest snapshot. Never replace
  // existing record metadata, history, selected pointers, or newer examples.
  const publishVersion = (
    record: RecordResponse,
    version: TemplateVersionControlResponse
  ) => {
    const templateCreationRecordId = record.template_creation_record.id;
    const merge = (current: RecordResponse | null | undefined) => {
      if (!current) {
        return {
          ...record,
          version_controls: record.version_controls.some((item) => item.id === version.id)
            ? record.version_controls.map((item) =>
                item.id === version.id ? version : item
              )
            : [...record.version_controls, version],
        };
      }
      if (current.template_creation_record.id !== templateCreationRecordId)
        return current;
      const existing = current.version_controls.find((item) => item.id === version.id);
      if (!existing)
        return { ...current, version_controls: [...current.version_controls, version] };
      if (
        existing.template_structure !== version.template_structure ||
        existing.example_note ||
        !version.example_note
      )
        return current;
      return {
        ...current,
        version_controls: current.version_controls.map((item) =>
          item.id === version.id ? { ...item, example_note: version.example_note } : item
        ),
      };
    };
    queryClient.setQueryData<RecordResponse | null>(options.queryKey, merge);
    queryClient.setQueryData<RecordResponse | null>(
      [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
      merge
    );
  };

  const bootstrap = (): Promise<RecordResponse> => {
    // A reopened actor can hold a null read while the prior PATCH fills this
    // cache and removes recovery state during its saved-structure confirmation.
    const cached = queryClient.getQueryData<RecordResponse | null>(options.queryKey);
    if (cached?.template_creation_record.template_id === template.id)
      return Promise.resolve(cached);
    let records = bootstraps.get(queryClient);
    if (!records) {
      records = new Map();
      bootstraps.set(queryClient, records);
    }
    const key = bootstrapKey(template.id, userRefId);
    const entry = records.get(key) ?? { resume: bootstrap };
    entry.resume = bootstrap;
    records.set(key, entry);
    if (entry.pending) return entry.pending;
    const pending = (async () => {
      // Cancellation stops generation, not the durable link of a created record.
      entry.record ??= (await ports.createRecord(template.id)).data;
      const data = entry.record;
      if (data.template_creation_record.user_ref_id !== userRefId) return data;
      const { data: linked } = await ports.updateRecord({
        templateCreationRecordId: data.template_creation_record.id,
        data: { template_id: template.id, template_name: confirmedTemplate.name },
      });
      const result = { ...data, template_creation_record: linked };
      if (!isLinkedSetup(result, template.id, userRefId))
        throw new Error('Preview setup link did not match its owner and template');
      return result;
    })();
    entry.pending = pending;
    void pending.then(
      (linked) => {
        entry.pending = undefined;
        // A foreign create is not a successful link: retain recovery so reopen
        // cannot repeat POST or publish the unlinked record in the lookup cache.
        if (!isLinkedSetup(linked, template.id, userRefId)) return;
        // Keep the durable link before cancellation or version preparation can
        // fail, without replacing a concurrent reader's metadata or history.
        queryClient.setQueryData<RecordResponse | null>(
          options.queryKey,
          (current) => current ?? linked
        );
        records.delete(key);
      },
      () => {
        entry.pending = undefined;
        if (!entry.record) records.delete(key);
      }
    );
    return pending;
  };

  return {
    resolveRecord: withDiagnostics('resolve_record', async (_: unknown, { signal }) => {
      const record = await queryClient.fetchQuery(options);
      assertActive(signal);
      return record;
    }),
    prepareVersion: withDiagnostics(
      'prepare_version',
      async (
        context: { record: RecordResponse | null },
        { signal }: { signal: AbortSignal }
      ) => {
        assertActive(signal);
        // Confirm even the saved-example shortcut: cached props can still describe
        // the old structure after chat has successfully changed the saved template.
        if (!(await confirmSavedStructure(signal))) return null;
        let record = context.record;
        const saved = record && findMatchingPreviewVersion(record, template);
        if (saved?.example_note) {
          preparedRecord = record;
          return saved;
        }
        if (
          !template.is_editable ||
          !confirmedTemplate.is_editable ||
          !template.structure_template
        )
          return null;
        if (record && record.template_creation_record.user_ref_id !== userRefId)
          return null;
        record ??= await bootstrap();
        assertActive(signal);
        if (record.template_creation_record.user_ref_id !== userRefId) return null;
        let version = findMatchingPreviewVersion(record, template);
        if (!version) {
          if (!(await confirmSavedStructure(signal)) || !confirmedTemplate.is_editable)
            return null;
          const { data } = await ports.createVersion({
            templateCreationRecordId: record.template_creation_record.id,
            data: { template_structure: template.structure_template },
            signal,
          });
          assertActive(signal);
          version = data;
        }
        preparedRecord = record;
        publishVersion(record, version);
        return version;
      }
    ),
    generateExample: withDiagnostics(
      'generate_example',
      async (
        context: { version: TemplateVersionControlResponse | null },
        { signal }: { signal: AbortSignal }
      ) => {
        assertActive(signal);
        const version = context.version;
        if (!version) return '';
        if (!(await confirmSavedStructure(signal))) return '';
        if (version.example_note) return version.example_note;
        if (!template.is_editable || !confirmedTemplate.is_editable) return '';
        if (!preparedRecord)
          failValidation('unprepared_record', 'Preview record was not prepared');
        const record = preparedRecord;
        const templateCreationRecordId = record.template_creation_record.id;
        return trackStreamRun(
          TrackingKey.TEMPLATE_EXAMPLE_GEN,
          { source: 'work_preview' },
          async (markFirstToken) => {
            let example = '';
            let completed = false;
            let failed = false;
            const onComplete = () => {
              completed = true;
            };
            const onFailure = () => {
              failed = true;
            };
            // Own the transport and accumulation, not the editor mutation/atoms. Abort
            // and late frames cannot mutate either saved-record cache identity.
            await streamExampleFromStructureGeneration(
              {
                data: {
                  template_creation_record_id: templateCreationRecordId,
                  version_control_id: version.id,
                },
                signal,
              },
              {
                onMessage: (chunk) => {
                  if (signal.aborted || failed || completed) return;
                  if (!example && chunk.data) markFirstToken();
                  example += chunk.data;
                  ports.onChunk(example);
                },
                onStreamErrorFrame: (frame) => {
                  onFailure();
                  if (signal.aborted) return;
                  // Error/message fields are open strings: emit only known codes.
                  logger.error('[TEMPLATE_PREVIEW_EXAMPLE] Stream error frame', {
                    code: knownStreamErrorCodes.has(frame.error)
                      ? frame.error
                      : 'unknown',
                  });
                },
                onUnparseableFrame: onFailure,
                onError: (error) => {
                  onFailure();
                  if (signal.aborted) return;
                  logger.error('[TEMPLATE_PREVIEW_EXAMPLE] Transport failed', {
                    error: sanitizeError(error),
                  });
                },
                onDone: onComplete,
                onClose: onComplete,
              }
            );
            assertActive(signal);
            if (failed) throw new Error('Example generation failed');
            // This example-note endpoint closes after persistence even in negotiated
            // v1; unlike structure conversion it emits no done frame. Legacy/on-prem
            // also terminates by clean close. Promise resolution alone (e.g. abort) is
            // never completion. Accept done for forward-compatible v1 transports.
            if (!completed || !example)
              failValidation('incomplete_stream', 'Example generation did not complete');
            // Clean close alone carries no canonical body: the server deidentifies
            // the accumulated note before persisting it. Read outside observed cache
            // and validate the target before publishing only its canonical example.
            const { data: persisted } = await getTemplateCreationRecordByTemplateId(
              template.id
            );
            assertActive(signal);
            const canonical = persisted.version_controls.find(
              (item) =>
                item.id === version.id &&
                item.template_creation_record_id === templateCreationRecordId &&
                item.template_structure === template.structure_template
            );
            if (
              persisted.template_creation_record.id !== templateCreationRecordId ||
              persisted.template_creation_record.template_id !== template.id ||
              !canonical?.example_note?.trim()
            )
              failValidation(
                'canonical_mismatch',
                'Completed example was not persisted for this preview'
              );
            if (!(await confirmSavedStructure(signal))) return '';
            publishVersion(record, {
              ...version,
              example_note: canonical.example_note,
            });
            return canonical.example_note;
          }
        );
      }
    ),
  };
}
